import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BookOpen, CircleDashed, Search, X } from "lucide-react";
import { usePageHeader } from "@/components/layouts/lib/page-header";
import { useAuth } from "@/App";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { AIDE_TOPICS, groupTopicsByModule, type AideTopic, type AideRole } from "@/content/aide";
import { searchAideTopics } from "@/content/aide/search";
import { AideMarkdownView } from "@/components/aide/AideMarkdownView";

// Static-markdown manual (mechanism-only pass — see content/aide/index.ts for
// the "why static, not DB-backed" note). Topics without content yet render an
// empty state rather than being hidden, so the page's full intended scope is
// visible even before every module is written up.
//
// Supports deep-linking via ?topic=<slug> so the contextual help trigger
// (HelpPanel's "Voir la page complète") and the header "?" button can land
// on a specific topic directly, not just the default first one.

function visibleTopics(topics: AideTopic[], role?: AideRole): AideTopic[] {
  return topics.filter((t) => !t.roles || (role && t.roles.includes(role)));
}

// Case-insensitive highlight only (search matching itself is accent-
// insensitive too, but mapping that back onto the original string's indices
// isn't worth the complexity here — an unaccented query just won't highlight
// an accented match, the result still shows up).
function highlightText(text: string, query: string) {
  const trimmed = query.trim();
  if (!trimmed) return text;
  const index = text.toLowerCase().indexOf(trimmed.toLowerCase());
  if (index === -1) return text;
  return (
    <>
      {text.slice(0, index)}
      <mark className="rounded bg-brand-gold-dark/20 text-inherit">
        {text.slice(index, index + trimmed.length)}
      </mark>
      {text.slice(index + trimmed.length)}
    </>
  );
}

export default function AidePage() {
  usePageHeader({ title: "Aide" });
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const topics = useMemo(() => visibleTopics(AIDE_TOPICS, user?.role), [user?.role]);
  const groups = useMemo(() => groupTopicsByModule(topics), [topics]);

  const [query, setQuery] = useState("");
  const searchResults = useMemo(() => searchAideTopics(topics, query), [topics, query]);
  const isSearching = query.trim().length > 0;

  const requestedSlug = searchParams.get("topic");
  const [activeSlug, setActiveSlugState] = useState(
    (requestedSlug && topics.some((t) => t.slug === requestedSlug) ? requestedSlug : topics[0]?.slug),
  );

  // If the query param changes (e.g. clicked "Voir la page complète" for a
  // different topic while already on /aide), follow it.
  useEffect(() => {
    if (requestedSlug && topics.some((t) => t.slug === requestedSlug)) {
      setActiveSlugState(requestedSlug);
    }
  }, [requestedSlug, topics]);

  function setActiveSlug(slug: string) {
    setActiveSlugState(slug);
    setSearchParams({ topic: slug }, { replace: true });
    setQuery("");
  }

  const active = topics.find((t) => t.slug === activeSlug) ?? topics[0];

  return (
    <div className="grid gap-5 lg:grid-cols-[260px_1fr] items-start">
      <aside className="bg-card border border-border rounded-lg p-3 lg:sticky lg:top-4">
        <div className="relative mb-3">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-subtle" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Rechercher dans l'aide..."
            className="h-9 pl-8 pr-8 text-[12px]"
          />
          {isSearching && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-subtle hover:text-body"
              title="Effacer la recherche"
            >
              <X size={13} />
            </button>
          )}
        </div>

        {isSearching ? (
          searchResults.length === 0 ? (
            <p className="px-2 py-3 text-[11.5px] text-muted-foreground">
              Aucun résultat pour « {query.trim()} ».
            </p>
          ) : (
            <nav className="space-y-0.5">
              {searchResults.map(({ topic, snippet, titleMatch }) => (
                <button
                  key={topic.slug}
                  onClick={() => setActiveSlug(topic.slug)}
                  className={cn(
                    "w-full flex items-start gap-2 rounded px-2 py-1.5 text-left transition-colors",
                    topic.slug === active?.slug
                      ? "bg-brand-gold-dark/10"
                      : "hover:bg-surface-muted",
                  )}
                >
                  {topic.content ? (
                    <BookOpen size={13} className="shrink-0 mt-0.5 text-subtle" />
                  ) : (
                    <CircleDashed size={13} className="shrink-0 mt-0.5 text-subtle" />
                  )}
                  <span className="min-w-0">
                    <span
                      className={cn(
                        "block truncate text-[12px]",
                        titleMatch
                          ? "font-medium text-brand-gold-dark"
                          : "font-medium text-body",
                      )}
                    >
                      {titleMatch ? highlightText(topic.title, query) : topic.title}
                    </span>
                    {snippet && (
                      <span className="block text-[10.5px] leading-snug text-muted-foreground line-clamp-2">
                        {highlightText(snippet, query)}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </nav>
          )
        ) : (
          groups.map((group) => (
            <div key={group.moduleGroup} className="mb-3 last:mb-0">
              <p className="px-2 mb-1 text-[10.5px] font-semibold uppercase tracking-wide text-subtle">
                {group.moduleGroup}
              </p>
              <nav className="space-y-0.5">
                {group.topics.map((topic) => (
                  <button
                    key={topic.slug}
                    onClick={() => setActiveSlug(topic.slug)}
                    className={cn(
                      "w-full flex items-center gap-2 rounded px-2 py-1.5 text-left text-[12px] transition-colors",
                      topic.slug === active?.slug
                        ? "bg-brand-gold-dark/10 text-brand-gold-dark font-medium"
                        : "text-body hover:bg-surface-muted",
                    )}
                  >
                    {topic.content ? (
                      <BookOpen size={13} className="shrink-0" />
                    ) : (
                      <CircleDashed size={13} className="shrink-0 text-subtle" />
                    )}
                    <span className="truncate">{topic.title}</span>
                  </button>
                ))}
              </nav>
            </div>
          ))
        )}
      </aside>

      <section className="bg-card border border-border rounded-lg px-6 py-6 min-h-[400px]">
        <AideMarkdownView content={active?.content ?? null} />
      </section>
    </div>
  );
}
